import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Monogram from "@/components/Monogram";
import Ornament from "@/components/Ornament";
import Footer from "@/components/Footer";
import CocktailCard from "@/components/CocktailCard";
import ProductGroup from "@/components/ProductGroup";
import SiblingNav from "@/components/SiblingNav";
import { useMenu } from "@/lib/api";
import LanguageDropdown from "@/components/LanguageDropdown";
import { useLang, t, catLabel } from "@/lib/i18n";

export default function CategoryPage() {
  const { slug } = useParams();
  const lang = useLang();
  const { data, isLoading: loading } = useMenu();
  const category = useMemo(() => data?.categories.find((c) => c.slug === slug) || null, [data, slug]);
  const siblings = useMemo(
    () => (category ? data.categories.filter((c) => c.kind === category.kind && c.slug !== slug) : []),
    [data, category]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const groups = useMemo(() => {
    if (!category) return [];
    const map = new Map();
    for (const prod of category.products || []) {
      const key = prod.subcategory || null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(prod);
    }
    return [...map.entries()];
  }, [category]);

  const isCocktail = category?.kind === "cocktail";

  return (
    <div
      data-testid={`category-page-${slug}`}
      className={`min-h-screen flex flex-col ${isCocktail ? "bg-[var(--cocktail-bg)]" : "texture-grain"}`}
    >
      <header
        className="sticky top-0 z-20 backdrop-blur-md border-b bg-[var(--header-bg)] border-[#C5A059]/25"
      >
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/menu"
            data-testid="back-to-menu"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--bordeaux)]"
          >
            <ArrowLeft className="h-4 w-4" /> Menu
          </Link>
          <div className="flex-1" />
          <LanguageDropdown />
          <Link to="/" data-testid="header-home-link" className="shrink-0">
            <Monogram size="sm" />
          </Link>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-4 pb-12">
        {loading && (
          <p className="text-center py-16 font-display italic text-xl text-[var(--muted)]">
            {t(lang, "loadingShort")}
          </p>
        )}

        {!loading && !category && (
          <div className="text-center py-20">
            <p className="font-display italic text-2xl text-[var(--muted)]">{t(lang, "notFound")}</p>
            <Link to="/menu" data-testid="not-found-back" className="text-[var(--bordeaux)] underline mt-3 inline-block">
              {t(lang, "backToMenu")}
            </Link>
          </div>
        )}

        {!loading && category && (
          <>
            <div className="pt-7 text-center">
              <h1
                data-testid="category-title"
                className="font-display font-semibold text-3xl sm:text-4xl text-[var(--bordeaux)]"
              >
                {catLabel(category.slug, lang)}
              </h1>
              <Ornament dark={isCocktail} />
            </div>

            {(category.products || []).length === 0 && (
              <div
                data-testid="category-empty"
                className="text-center py-14 border rounded-xl border-[var(--line)] text-[var(--muted)] bg-[var(--card)]"
              >
                <p className="font-display italic text-xl">{t(lang, "empty")}</p>
              </div>
            )}

            {isCocktail ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-4">
                {(category.products || []).map((cocktail, i) => (
                  <div key={cocktail.id} className="animate-fade-up" style={{ animationDelay: `${Math.min(i * 0.06, 0.5)}s` }}>
                    <CocktailCard cocktail={cocktail} />
                  </div>
                ))}
              </div>
            ) : (
              groups.map(([sub, products]) => (
                <ProductGroup key={sub || "main"} sub={sub} products={products} lang={lang} />
              ))
            )}

            {siblings.length > 0 && <SiblingNav siblings={siblings} isCocktail={isCocktail} lang={lang} />}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
